import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../utils/api';

const initialState = {
    broadcasts: [],
    loading: false,
    error: null,
    pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0
    }
};

// Async thunk to fetch organization broadcasts
export const fetchBroadcasts = createAsyncThunk(
    'broadcasts/fetchBroadcasts',
    async (params, { rejectWithValue }) => {
        try {
            const response = await api.get('/organization/broadcasts', { params });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch broadcasts');
        }
    }
);

// Async thunk to send a broadcast
export const sendBroadcast = createAsyncThunk(
    'broadcasts/sendBroadcast',
    async (broadcastData, { rejectWithValue }) => {
        try {
            const response = await api.post('/organization/broadcasts', broadcastData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to send broadcast');
        }
    }
);

// Async thunk to delete a broadcast
export const deleteBroadcast = createAsyncThunk(
    'broadcasts/deleteBroadcast',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/organization/broadcasts/${id}`);
            return { id, message: response.data.message };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete broadcast');
        }
    }
);

const broadcastSlice = createSlice({
    name: 'broadcasts',
    initialState,
    reducers: {
        clearBroadcastError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Broadcasts
            .addCase(fetchBroadcasts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBroadcasts.fulfilled, (state, action) => {
                state.loading = false;
                state.broadcasts = action.payload.data;
                state.pagination = {
                    currentPage: action.payload.meta.current_page,
                    lastPage: action.payload.meta.last_page,
                    total: action.payload.meta.total
                };
            })
            .addCase(fetchBroadcasts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Send Broadcast
            .addCase(sendBroadcast.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(sendBroadcast.fulfilled, (state, action) => {
                state.loading = false;
                state.broadcasts.unshift(action.payload.data);
            })
            .addCase(sendBroadcast.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Delete Broadcast
            .addCase(deleteBroadcast.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteBroadcast.fulfilled, (state, action) => {
                state.loading = false;
                state.broadcasts = state.broadcasts.filter(b => b.id !== action.payload.id);
            })
            .addCase(deleteBroadcast.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { clearBroadcastError } = broadcastSlice.actions;
export default broadcastSlice.reducer;
